import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Upload, FileText, Home, Building2, Check, Clock, XCircle, Loader2, Bell, BellOff } from "lucide-react";
import { enablePushNotifications, disablePushNotifications } from "@/lib/push";
import { isPushConfigured } from "@/lib/firebase";

const DOC_TYPES = [
  { key: "rg", label: "RG (com foto)", icon: FileText, roles: ["acougueiro"] },
  { key: "residence", label: "Comprovante de residência", icon: Home, roles: ["acougueiro", "comercio"] },
  { key: "cnpj_card", label: "Cartão CNPJ", icon: Building2, roles: ["comercio"] },
];

const STATUS_MAP = {
  pending_review: { label: "Em análise", icon: Clock, color: "text-amber-400 bg-amber-950/40" },
  approved: { label: "Aprovado", icon: Check, color: "text-emerald-400 bg-emerald-950/40" },
  rejected: { label: "Rejeitado", icon: XCircle, color: "text-red-400 bg-red-950/40" },
};

export default function Profile() {
  const { user, refresh } = useAuth();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState({});
  const [pushStatus, setPushStatus] = useState({ available: false, device_count: 0 });
  const [pushBusy, setPushBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/documents");
      setDocs(data.documents || []);
    } catch {}
    try {
      const { data } = await api.get("/push/status");
      setPushStatus(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushStatus.device_count > 0) {
        await disablePushNotifications();
      } else {
        await enablePushNotifications();
      }
      const { data } = await api.get("/push/status");
      setPushStatus(data);
    } finally { setPushBusy(false); }
  };

  const upload = async (docType, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Arquivo maior que 5MB");
    setUploading(u => ({ ...u, [docType]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents/upload?doc_type=${docType}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento enviado! Análise em até 48h.");
      load(); refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(u => ({ ...u, [docType]: false })); }
  };

  const relevantTypes = DOC_TYPES.filter(t => t.roles.includes(user?.role));

  return (
    <div className="space-y-6 anim-fade-up max-w-4xl">
      <div>
        <h1 className="text-3xl font-display font-extrabold">Meu Perfil</h1>
        <p className="text-zinc-400 text-sm mt-1">Complete seu perfil para ganhar o Selo Verificado azul.</p>
      </div>

      {/* Verified badge status */}
      <div className={`cw-card p-6 flex items-center gap-4 ${user?.is_verified ? "bg-gradient-to-br from-zinc-900 to-blue-950/30" : ""}`}>
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${user?.is_verified ? "bg-blue-500/20" : "bg-zinc-800"}`}>
          <ShieldCheck className={`w-7 h-7 ${user?.is_verified ? "text-blue-400" : "text-zinc-500"}`} />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-lg">
            {user?.is_verified ? "Perfil Verificado ✓" : "Perfil não verificado"}
          </div>
          <div className="text-sm text-zinc-400">
            {user?.is_verified
              ? "Você tem o selo azul de verificação. Isso gera mais confiança e prioridade nas vagas."
              : `Envie ${user?.role === "comercio" ? "cartão CNPJ + comprovante" : "RG + comprovante de residência"} para conquistar o selo.`}
          </div>
        </div>
      </div>

      {/* Push notifications */}
      <div className="cw-card p-6 flex items-center gap-4" data-testid="push-card">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${pushStatus.device_count > 0 ? "bg-emerald-500/20" : "bg-zinc-800"}`}>
          {pushStatus.device_count > 0
            ? <Bell className="w-7 h-7 text-emerald-400" />
            : <BellOff className="w-7 h-7 text-zinc-500" />}
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-lg">
            {pushStatus.device_count > 0 ? "Notificações ativas" : "Notificações push"}
          </div>
          <div className="text-sm text-zinc-400">
            {!isPushConfigured() || !pushStatus.available
              ? "As notificações push ainda não estão configuradas neste servidor."
              : pushStatus.device_count > 0
                ? `Este e mais ${Math.max(pushStatus.device_count - 1, 0)} dispositivo(s) receberão avisos de novas diárias, ofertas e mensagens.`
                : "Ative para receber avisos de novas diárias, contra-ofertas e mensagens em tempo real."}
          </div>
        </div>
        <button
          onClick={togglePush}
          disabled={pushBusy || !isPushConfigured() || !pushStatus.available}
          data-testid="btn-toggle-push"
          className={pushStatus.device_count > 0 ? "cw-btn-outline text-sm" : "cw-btn-primary text-sm"}>
          {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (pushStatus.device_count > 0 ? "Desativar" : "Ativar")}
        </button>
        {pushStatus.device_count > 0 && (
          <button
            onClick={async () => {
              try {
                const { data } = await api.post("/push/test");
                if (data.sent > 0) toast.success(`Notificação enviada para ${data.sent} dispositivo(s)`);
                else toast("Nenhum dispositivo ativo respondeu");
              } catch (e) {
                toast.error("Falha ao enviar teste");
              }
            }}
            data-testid="btn-test-push"
            className="cw-btn-outline text-sm">
            Testar
          </button>
        )}
      </div>

      {/* Info */}
      <div className="cw-card p-6">
        <h3 className="font-display font-bold mb-4">Dados cadastrais</h3>
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div><dt className="text-zinc-500 text-xs uppercase">Nome</dt><dd>{user?.name}</dd></div>
          <div><dt className="text-zinc-500 text-xs uppercase">Email</dt><dd>{user?.email}</dd></div>
          <div><dt className="text-zinc-500 text-xs uppercase">{user?.role === "comercio" ? "CNPJ" : "CPF"}</dt><dd className="font-mono">{user?.document || "—"}</dd></div>
          <div><dt className="text-zinc-500 text-xs uppercase">Cidade</dt><dd>{user?.city || "—"}</dd></div>
          {user?.role === "comercio" && <div><dt className="text-zinc-500 text-xs uppercase">Estabelecimento</dt><dd>{user?.business_name}</dd></div>}
          {user?.role === "acougueiro" && <div><dt className="text-zinc-500 text-xs uppercase">Especialidade</dt><dd>{user?.specialty} disponível</dd></div>}
        </dl>
      </div>

      {/* Documents upload */}
      <div>
        <h3 className="font-display font-bold text-xl mb-4">Documentos</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {relevantTypes.map(t => {
            const existing = docs.find(d => d.doc_type === t.key);
            const stat = existing ? STATUS_MAP[existing.status] : null;
            return (
              <div key={t.key} className="cw-card p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/15 flex items-center justify-center">
                    <t.icon className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{t.label}</div>
                    {existing && stat && (
                      <span className={`badge-pill mt-1 ${stat.color}`}>
                        <stat.icon className="w-3 h-3" /> {stat.label}
                      </span>
                    )}
                  </div>
                </div>
                {existing && (
                  <div className="text-xs text-zinc-500 mb-3 truncate">{existing.original_filename}</div>
                )}
                <label className="cw-btn-outline text-sm w-full cursor-pointer flex items-center justify-center gap-2" data-testid={`upload-${t.key}`}>
                  {uploading[t.key] ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando…</> : <>
                    <Upload className="w-4 h-4" /> {existing ? "Substituir" : "Enviar"}
                  </>}
                  <input type="file" accept="image/*,application/pdf" hidden
                         onChange={e => upload(t.key, e.target.files?.[0])} />
                </label>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-zinc-500 mt-3">Formatos aceitos: JPG, PNG, WEBP, PDF. Máx 5MB. Análise em até 48h.</p>
      </div>
    </div>
  );
}
